import { CustomFormGroup } from "components";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import apiService from "services/api";
import { lessonCategories } from "pages/Course/constants";
import { capitalize } from "utils/capitalize";
import Select from "react-select";
import { Button } from "reactstrap";
import { useNotificationContext } from "contexts";

const formInitialState = {
	student: undefined,
	course: undefined,
	lesson: undefined,
};

const groupStyles = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
};
const groupBadgeStyles = {
	backgroundColor: "#EBECF0",
	borderRadius: "2em",
	color: "#172B4D",
	display: "inline-block",
	fontSize: 12,
	fontWeight: "normal",
	lineHeight: "1",
	minWidth: 1,
	padding: "0.16666666666667em 0.5em",
	textAlign: "center",
};

const formatGroupLabel = (data) => (
	<div style={groupStyles}>
		<span>{data.label}</span>
		<span style={groupBadgeStyles}>{data.options.length}</span>
	</div>
);

const AddLessonRecord = () => {
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset,
		getValues,
		control,
		formState: { errors },
	} = useForm({
		defaultValues: formInitialState,
	});

	const { createNotification } = useNotificationContext();

	const selectedCourse = watch("course");
	const selectedStudent = watch("student");

	const { data: selectedStudentData } = useQuery(["student", selectedStudent], () => apiService.student.get(selectedStudent?.data?.id), {
		enabled: selectedStudent !== undefined,
	});
	const courseOptions = useMemo(
		() => selectedStudentData?.courses?.map((course) => ({ label: course.courseDetail.name, value: course.courseDetail.id })) || [],
		[selectedStudentData]
	);

	const { data: studentOptions, refetch: refetchStudents } = useQuery(["student"], () =>
		apiService.student.getAbsoluteMin().then((data) => {
			const currentStudent = getValues("student");
			if (selectedCourse) {
				const newStudent = data.find((student) => student.id === currentStudent.value);
				setValue("student", {
					value: newStudent.id,
					label: newStudent.firstName + " " + newStudent.lastName + " (" + newStudent?.centre?.shortName + ")",
					data: newStudent,
				});
				refetchLessons();
			}
			return data.map((c) => ({
				value: c.id,
				label: c.firstName + " " + c.lastName,
				data: c,
			}));
		})
	);

	const { data: lessonOptions, refetch: refetchLessons } = useQuery(
		["lesson", selectedCourse],
		() =>
			apiService.course.get(selectedCourse).then((course) => {
				const completedLessons = selectedStudentData.lessonRecords?.map((lessonRecord) => lessonRecord?.lessonDetail?.lessonId);

				const legacyLessons = selectedStudentData.legacyCompletedLessonRecords?.map((legacyLesson) => legacyLesson.lessonId);

				return lessonCategories.map((category) => ({
					label: capitalize(category),
					options: course.lessons
						?.filter((lesson) => !completedLessons.includes(lesson.id) && !legacyLessons.includes(lesson.id))
						.map((lesson) => ({
							value: lesson,
							label: `${lesson.code} - ${lesson.name}`,
						}))
						.filter((lesson) => lesson?.value?.category === category),
				}));
			}),
		{ enabled: Boolean(selectedCourse) && selectedStudentData !== undefined }
	);

	const addPreviousLessonRecord = (data) => {
		const formBody = {
			lessonId: data.lesson.id,
			studentId: data.student.value,
		};

		apiService.lessonRecord.legacy
			.create(formBody)
			.then((data) => {
				createNotification({
					type: "success",
					message: "Successfully added previous lesson record",
				});
				setValue("lesson", undefined);
				refetchStudents();
			})
			.catch((error) => createNotification({ type: "danger", message: error.message }));
	};

	return (
		<div className="content">
			<div className="d-flex justify-content-center card">
				<div className="card-body p-sm-4">
					<div className="text-center mb-4 font-weight-bold display-5 text-white mt-2">Add Previous Lesson Record</div>
					<form onSubmit={handleSubmit(addPreviousLessonRecord)} className="mt-3">
						<CustomFormGroup error={errors.student} formLabel="Student">
							<Controller
								control={control}
								name="student"
								rules={{
									required: {
										value: true,
										message: "Select a student",
									},
								}}
								render={({ field: { onChange, value, ref } }) => (
									<Select
										inputRef={ref}
										className="react-select info"
										classNamePrefix="react-select"
										value={studentOptions?.find((c) => c.value === value?.value)}
										onChange={(val) => {
											onChange(val);
										}}
										placeholder="Select student...."
										options={studentOptions || []}
									/>
								)}
							/>
						</CustomFormGroup>
						<CustomFormGroup error={errors.course} formLabel="Course">
							<Controller
								control={control}
								name="course"
								rules={{
									required: {
										value: true,
										message: "Add a course",
									},
								}}
								render={({ field: { onChange, value, ref } }) => (
									<Select
										inputRef={ref}
										className="react-select info"
										classNamePrefix="react-select"
										value={courseOptions?.find((c) => c.value === value)}
										onChange={(val) => {
											onChange(val.value);
											setValue("lesson", null);
										}}
										placeholder="Select course...."
										options={courseOptions || []}
									/>
								)}
							/>
						</CustomFormGroup>
						<CustomFormGroup error={errors.lesson} formLabel="Lesson">
							<Controller
								control={control}
								name="lesson"
								rules={{
									required: {
										value: true,
										message: "Add a lesson",
									},
								}}
								render={({ field: { onChange, value, ref } }) => (
									<Select
										inputRef={ref}
										className="react-select info"
										key={value}
										menuPlacement="auto"
										classNamePrefix="react-select"
										value={lessonOptions
											?.reduce((lessons, currentGroup) => lessons.concat(...currentGroup.options), [])
											.find((c) => c.value?.id === value?.id)}
										onChange={(val) => onChange(val.value)}
										placeholder="Select lesson...."
										options={lessonOptions}
										formatGroupLabel={formatGroupLabel}
									/>
								)}
							/>
						</CustomFormGroup>
						<small className="text-warning ml-2">
							Note: this is only for adding previous records before the dashboard system was introduced.
						</small>
						<div className="d-flex justify-content-end">
							<Button color="success" type="submit">
								Add
							</Button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default AddLessonRecord;
